import {
  CLUB_LEVELS,
  FACULTIES,
  MAX_EVENT_AHEAD_MS,
  MAX_EVENT_MINUTES,
  MAX_EVENT_TITLE,
  MAX_GOAL_TITLE,
  MIN_EVENT_MINUTES,
  OPEN_ROOM_IDS,
  ROOMS,
  type ClubEventDto,
  type ClubGoalDto,
  type ClubPageDto,
} from "@clubap/shared";
import { useEffect, useState } from "react";
import {
  cancelClubEvent,
  createClubEvent,
  fetchClubEvents,
  fetchClubPage,
  setClubGoal,
} from "../social/clubsApi.js";
import { ClubMark } from "./ClubBadges.js";

interface Props {
  clubId: string;
  onClose: () => void;
  /** Llevar al jugador a la sede. Quien monta el panel sabe cómo se cruza; esto sólo pide. */
  onEnterRoom: (clubId: string) => void;
}

/**
 * Las salas donde se puede convocar algo: las ABIERTAS del campus, nunca la sede.
 *
 * Es lo que dice `ClubEventDto.roomId` — un evento es una excusa para juntarse donde se
 * ve, y citar en la propia sede sería citar donde ya están. Se arma una vez al cargar el
 * módulo porque el catálogo de salas es estático: lo reescribe el editor y reinicia todo.
 */
const SALAS: ReadonlyArray<{ id: string; name: string }> = OPEN_ROOM_IDS.map((id) => ({
  id,
  name: ROOMS[id].name,
}));

function nombreDeSala(roomId: string): string {
  return SALAS.find((sala) => sala.id === roomId)?.name ?? roomId;
}

/** El nombre de una facultad, o "Transversal" cuando el club no es de ninguna. */
function facultad(id: string | null): string {
  if (id === null) return "Transversal";
  return FACULTIES.find((entry) => entry.id === id)?.name ?? id;
}

function cuando(at: number): string {
  return new Date(at).toLocaleString("es", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dia(at: number): string {
  return new Date(at).toLocaleDateString("es", { day: "numeric", month: "short" });
}

/** La hora local en el formato que `datetime-local` entiende. El `toISOString` va en UTC. */
function paraInput(at: number): string {
  const fecha = new Date(at);
  const dd = (n: number) => String(n).padStart(2, "0");
  return `${fecha.getFullYear()}-${dd(fecha.getMonth() + 1)}-${dd(fecha.getDate())}T${dd(
    fecha.getHours(),
  )}:${dd(fecha.getMinutes())}`;
}

/**
 * Convocar un evento. Sólo se monta si el servidor dijo `canModerate`.
 *
 * Va en su propio componente por su estado: montado con `key={clubId}`, cambiar de club
 * lo vacía solo, y si viviera en la ficha habría que acordarse de limpiarlo a mano — que
 * es exactamente el descuido con el que un borrador se lleva a la pantalla siguiente.
 */
function EventForm({
  clubId,
  onCreated,
}: {
  clubId: string;
  onCreated: (events: ClubEventDto[]) => void;
}) {
  const [title, setTitle] = useState("");
  const [roomId, setRoomId] = useState<string>(SALAS[0]?.id ?? "");
  const [cuandoInput, setCuandoInput] = useState(() => paraInput(Date.now() + 60 * 60 * 1000));
  const [minutes, setMinutes] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startsAt = new Date(cuandoInput).getTime();
  const valido =
    title.trim().length > 0 &&
    Number.isFinite(startsAt) &&
    minutes >= MIN_EVENT_MINUTES &&
    minutes <= MAX_EVENT_MINUTES;

  const convocar = async () => {
    setBusy(true);
    setError(null);

    try {
      const result = await createClubEvent(clubId, {
        title: title.trim(),
        roomId,
        startsAt,
        minutes,
      });
      onCreated(result.events);
      setTitle("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo convocar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="club-forma"
      onSubmit={(event) => {
        event.preventDefault();
        if (valido) void convocar();
      }}
    >
      {error && (
        <p className="sheet__error" role="alert">
          {error}
        </p>
      )}

      <input
        className="join__input"
        value={title}
        maxLength={MAX_EVENT_TITLE}
        placeholder="Qué se convoca…"
        aria-label="Título del evento"
        onChange={(event) => setTitle(event.target.value)}
      />

      <div className="club-forma__fila">
        <select
          className="join__input"
          value={roomId}
          aria-label="Dónde"
          onChange={(event) => setRoomId(event.target.value)}
        >
          {SALAS.map((sala) => (
            <option key={sala.id} value={sala.id}>
              {sala.name}
            </option>
          ))}
        </select>

        <input
          className="join__input"
          type="number"
          value={minutes}
          min={MIN_EVENT_MINUTES}
          max={MAX_EVENT_MINUTES}
          step={5}
          aria-label="Cuántos minutos dura"
          onChange={(event) => setMinutes(Number.parseInt(event.target.value, 10) || 0)}
        />
      </div>

      <input
        className="join__input"
        type="datetime-local"
        value={cuandoInput}
        min={paraInput(Date.now())}
        max={paraInput(Date.now() + MAX_EVENT_AHEAD_MS)}
        aria-label="Día y hora"
        onChange={(event) => setCuandoInput(event.target.value)}
      />

      <div className="club-acciones">
        <button className="btn btn--small" type="submit" disabled={busy || !valido}>
          {busy ? "…" : "Convocar"}
        </button>
        <span className="sheet__hint">
          De {MIN_EVENT_MINUTES} a {MAX_EVENT_MINUTES} minutos, y como mucho dentro de dos
          semanas.
        </span>
      </div>
    </form>
  );
}

/** Fijar la meta de recaudación. Como el formulario de eventos: sólo con `canModerate`. */
function GoalForm({
  clubId,
  goal,
  onSaved,
}: {
  clubId: string;
  goal: ClubGoalDto | null;
  onSaved: (goal: ClubGoalDto) => void;
}) {
  const [title, setTitle] = useState(goal?.title ?? "");
  const [target, setTarget] = useState(String(goal?.target ?? 500));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cifra = Number.parseInt(target, 10);
  const valido = title.trim().length > 0 && Number.isInteger(cifra) && cifra > 0;

  const guardar = async () => {
    setBusy(true);
    setError(null);

    try {
      onSaved(await setClubGoal(clubId, { title: title.trim(), target: cifra }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo fijar la meta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="club-forma"
      onSubmit={(event) => {
        event.preventDefault();
        if (valido) void guardar();
      }}
    >
      {error && (
        <p className="sheet__error" role="alert">
          {error}
        </p>
      )}

      <div className="club-forma__fila">
        <input
          className="join__input"
          value={title}
          maxLength={MAX_GOAL_TITLE}
          placeholder="Para qué se junta…"
          aria-label="Título de la meta"
          onChange={(event) => setTitle(event.target.value)}
        />
        <input
          className="join__input"
          type="number"
          value={target}
          min={1}
          aria-label="Cuántas monedas"
          onChange={(event) => setTarget(event.target.value)}
        />
      </div>

      <div className="club-acciones">
        <button className="btn btn--small" type="submit" disabled={busy || !valido}>
          {busy ? "…" : goal ? "Cambiar la meta" : "Fijar la meta"}
        </button>
      </div>
    </form>
  );
}

/**
 * La ficha de un club: quién está, qué se ha convocado, para qué se junta dinero y qué
 * ha pasado.
 *
 * Todo lo que es de presidente sale de `canModerate`, que lo manda el servidor. Acá NO se
 * compara ningún papel: si mañana un admin pudiera moderar, o dejara de poder, esta
 * pantalla no tendría que enterarse — quien contesta esa pregunta es
 * `auth/permissions.ts` y nadie más. Y esconder un botón nunca es la defensa: cada
 * endpoint vuelve a preguntar.
 */
export function ClubPage({ clubId, onClose, onEnterRoom }: Props) {
  const [page, setPage] = useState<ClubPageDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Depende del ID del club y de nada más. Depender de un objeto —el club, la cuenta—
     volvería a pedir la ficha en cada render que cambiara su referencia, que es el bug
     de las 97 reconexiones con otra ropa. */
  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);

    void (async () => {
      try {
        const dto = await fetchClubPage(clubId);
        if (!cancelled) setPage(dto);
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "No se pudo abrir el club.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const conEventos = (events: ClubEventDto[]) => {
    setPage((actual) => (actual ? { ...actual, events } : actual));
  };

  const cancelar = async (event: ClubEventDto) => {
    setBusy(event.id);
    setError(null);

    try {
      await cancelClubEvent(event.id);
      /* Cancelar contesta `{ ok: true }`, así que la lista hay que volver a pedirla.
         Sólo los eventos: recargar la ficha entera perdería el scroll del tablón. */
      conEventos(await fetchClubEvents(clubId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo cancelar.");
    } finally {
      setBusy(null);
    }
  };

  const club = page?.club;
  const siguiente = page ? CLUB_LEVELS.find((paso) => paso.level === page.level + 1) : undefined;
  const meta = page?.goal ?? null;
  const avance = meta && meta.target > 0 ? Math.min(1, meta.treasury / meta.target) : 0;

  return (
    <div className="overlay">
      <div className="panel sheet club-sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">{club?.code ?? "Club"}</h2>
          {page && <span className="sheet__coins">◉ {page.treasury}</span>}
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        {!page && !error && <p className="sheet__hint">Abriendo la ficha del club…</p>}

        {page && club && (
          <>
            <div className="club-cabecera">
              <span
                className="badge club-marca club-marca--grande"
                style={{ color: club.color, borderColor: club.color }}
              >
                <ClubMark club={club} size={52} />
              </span>

              <div className="club-cabecera__texto">
                <p className="club-siglas">{club.code}</p>
                <h3 className="club-nombre">{club.name}</h3>
                <p className="social__blurb">{page.summary.blurb || club.blurb}</p>
              </div>
            </div>

            <p className="club-meta">
              <span>{facultad(club.facultyId)}</span>
              <span>·</span>
              <span title={siguiente ? `Siguiente: ${siguiente.unlock}` : "Nivel máximo"}>
                Nivel {page.level}
              </span>
              <span>·</span>
              <span>{page.rank === null ? "Sin puesto todavía" : `Puesto ${page.rank}`}</span>
              <span>·</span>
              <span title="Puntos de esta temporada">{page.seasonPoints} pts</span>
            </p>

            <div className="club-acciones">
              {page.member ? (
                <button className="btn" type="button" onClick={() => onEnterRoom(clubId)}>
                  Entrar a la sede
                </button>
              ) : (
                <span className="sheet__hint">
                  La sede es de los miembros. Pide entrada desde la pestaña de clubes.
                </span>
              )}
            </div>

            <section className="sheet__group">
              <h3 className="sheet__slot">
                Miembros <span className="sheet__worn">{page.roster.length}</span>
              </h3>

              {page.roster.length === 0 ? (
                <p className="sheet__hint">Nadie se ha unido todavía.</p>
              ) : (
                <ul className="sheet__list">
                  {page.roster.map((member) => (
                    <li className="social__row" key={member.id}>
                      <span className="social__pick social__pick--static">
                        <span className="social__name">{member.username}</span>
                      </span>
                      {member.role === "presidente" && (
                        <span className="sheet__badge" title="Preside este club">
                          Preside
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="sheet__group">
              <h3 className="sheet__slot">Por venir</h3>

              {page.events.length === 0 ? (
                <p className="sheet__hint">No hay nada convocado.</p>
              ) : (
                <ul className="sheet__list">
                  {page.events.map((event) => (
                    <li className="sheet__item club-evento" key={event.id}>
                      <span className="sheet__name">
                        {event.title}
                        <span className="social__blurb">
                          {nombreDeSala(event.roomId)} · {cuando(event.startsAt)}
                        </span>
                      </span>

                      {event.attended && <span className="sheet__badge">fuiste</span>}
                      <span className="social__muted" title="Cuántos han aparecido">
                        ◍ {event.attendance}
                      </span>

                      {page.canModerate && (
                        <button
                          className="social__quiet"
                          type="button"
                          disabled={busy !== null}
                          title={`Cancelar ${event.title}`}
                          onClick={() => void cancelar(event)}
                        >
                          {busy === event.id ? "…" : "Cancelar"}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {page.canModerate && <EventForm key={clubId} clubId={clubId} onCreated={conEventos} />}
            </section>

            <section className="sheet__group">
              <h3 className="sheet__slot">Meta</h3>

              {meta ? (
                <>
                  <p className="club-meta__titulo">
                    {meta.title}
                    <span className="social__muted">
                      ◉ {meta.treasury} / {meta.target}
                    </span>
                  </p>
                  <div
                    className="club-barra"
                    role="progressbar"
                    aria-valuenow={Math.round(avance * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${meta.title}: ${meta.treasury} de ${meta.target}`}
                  >
                    <span
                      className={`club-barra__relleno${meta.reachedAt ? " club-barra__relleno--llena" : ""}`}
                      style={{ width: `${avance * 100}%`, background: club.color }}
                    />
                  </div>
                  {meta.reachedAt && (
                    <p className="sheet__hint">Se alcanzó el {dia(meta.reachedAt)}.</p>
                  )}
                </>
              ) : (
                <p className="sheet__hint">Este club no está juntando para nada en concreto.</p>
              )}

              {page.canModerate && (
                <GoalForm
                  key={clubId}
                  clubId={clubId}
                  goal={meta}
                  onSaved={(goal) =>
                    setPage((actual) => (actual ? { ...actual, goal } : actual))
                  }
                />
              )}
            </section>

            <section className="sheet__group">
              <h3 className="sheet__slot">Tablón</h3>

              {page.announcements.length === 0 ? (
                <p className="sheet__hint">Todavía no hay anuncios.</p>
              ) : (
                <div className="social__log">
                  {page.announcements.map((entry) => (
                    <p className="chat__line" key={entry.id} title={cuando(entry.createdAt)}>
                      {/* Sin firma cuando el autor borró su cuenta: el anuncio le pasó al
                          club y sigue valiendo. */}
                      <span className="chat__who">{entry.author?.username ?? "—"}</span>
                      <span className="chat__text">{entry.body}</span>
                    </p>
                  ))}
                </div>
              )}
            </section>

            <section className="sheet__group">
              <h3 className="sheet__slot">Bitácora</h3>

              {page.log.length === 0 ? (
                <p className="sheet__hint">No ha pasado nada todavía.</p>
              ) : (
                <ul className="club-bitacora">
                  {page.log.map((entry) => (
                    <li className="club-bitacora__linea" key={entry.id}>
                      <span className="social__muted">{entry.kind}</span>
                      {/* El texto ya viene resuelto: la plantilla se aplicó al ESCRIBIR la
                          línea, así que dice lo que pasó entonces y no lo que diría hoy. */}
                      <span className="club-bitacora__texto">{entry.detail}</span>
                      <span className="social__muted">{dia(entry.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
